/**
 * Meluya Bundle — hijacks the standard product Add-to-Cart submit on bundle
 * PDPs and replaces the single placeholder-variant add with a bulk
 * /cart/add.js call containing every slot (one cart line per quantity unit).
 *
 * Per-slot variant_id is resolved live from the dropdowns, falling back to
 * the metafield default. Cart lines tagged with properties._meluya_bundle_id
 * are picked up by the discount function in the meluya-app repo.
 */
(function () {
  if (window.__meluyaBundleInit) return;
  window.__meluyaBundleInit = true;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const log = (...args) => console.log('[meluya-bundle]', ...args);
  const warn = (...args) => console.warn('[meluya-bundle]', ...args);

  const moneyFormat = (cents) => {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    // Prefer Shopify's formatter so it matches the shop's money_format
    // setting exactly (which is what Liquid's `| money` filter uses on the
    // server-side initial render).
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    return `${(cents / 100).toFixed(2).replace('.', ',')}€`;
  };

  function init() {
    const cfgEl = document.querySelector('[data-meluya-bundle-config]');
    if (!cfgEl) return;

    let cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (err) {
      warn('failed to parse config', err);
      return;
    }
    if (!cfg || !Array.isArray(cfg.components) || cfg.components.length === 0) return;

    log('init', { rule_id: cfg.rule_id, components: cfg.components.length });

    // Limit our reach to the section containing the bundle config. The
    // cart-drawer's quick-buy modal injects ATC forms for upsell products
    // into the DOM at runtime; without this scope, the MutationObserver
    // below hijacks those forms and turns them into bundle submits (which
    // duplicates the bundle instead of adding the upsell product).
    const bundleSection = cfgEl.closest('.shopify-section') || document.body;
    // Skip quick-buy upsell forms — the PDP's own form is wrapped in
    // <product-rerender> too, so we can't blanket-exclude product-rerender.
    const isBundleForm = (form) =>
      bundleSection.contains(form) &&
      !form.closest('quick-buy-modal, [id^="quick-buy-modal-content"]');

    const slots = Array.from(document.querySelectorAll('[data-meluya-bundle-slot]'));
    log('slots:', slots.length);

    // Disable bundle ATC buttons (incl. sticky-ATC buttons targeting the form
    // via `form="<id>"`) whenever any slot's currently-resolved variant is
    // unavailable. Slots flip data-variant-available in their onChange.
    function syncAtcButton() {
      const anyUnavailable = slots.some((s) => s.dataset.variantAvailable === '0');
      const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]')).filter(isBundleForm);
      const buttons = new Set();
      forms.forEach((form) => {
        form.querySelectorAll('button[type="submit"], [type="submit"]').forEach((b) => buttons.add(b));
        if (form.id) {
          document
            .querySelectorAll(`button[type="submit"][form="${form.id}"], [type="submit"][form="${form.id}"]`)
            .forEach((b) => buttons.add(b));
        }
      });
      buttons.forEach((b) => {
        if (anyUnavailable) {
          b.setAttribute('disabled', 'disabled');
          b.setAttribute('aria-disabled', 'true');
          b.classList.add('button--disabled', 'is-disabled');
        } else {
          b.removeAttribute('disabled');
          b.removeAttribute('aria-disabled');
          b.classList.remove('button--disabled', 'is-disabled');
        }
      });
    }

    slots.forEach((slot) => {
      const slotIdx = slot.dataset.slotIdx;
      const variantsEl = document.querySelector(
        `script[data-meluya-bundle-variants][data-slot-idx="${slotIdx}"]`
      );
      if (!variantsEl) return;

      let variants;
      try {
        variants = JSON.parse(variantsEl.textContent);
      } catch (err) {
        warn('failed to parse variants for slot', slotIdx, err);
        return;
      }

      const selects = Array.from(slot.querySelectorAll('[data-meluya-bundle-option]'));
      if (selects.length === 0) return;

      const variantOpts = (v) => [v.option1, v.option2, v.option3].filter((o) => o != null);

      // The color axis is the "primary" — it always shows every color that
      // exists in at least one available variant. All non-color axes are
      // filtered against the currently chosen color, so picking Blush
      // collapses the size dropdown to just the sizes that exist in Blush.
      const colorIdx = selects.findIndex((s) => {
        const label = (s.getAttribute('aria-label') || '').toLowerCase();
        return label.includes('farbe') || label.includes('color') || label.includes('colour');
      });

      const hasAvailableCombo = (chosen) =>
        variants.some((v) => {
          const opts = variantOpts(v);
          if (opts.length !== chosen.length) return false;
          if (!opts.every((o, i) => o === chosen[i])) return false;
          return v.available;
        });

      // Disable options that the user cannot reach:
      //  - Color axis (or single-axis product): disable values with no
      //    available variant anywhere at that position.
      //  - Non-color axis: disable values that have no available variant
      //    in combination with the currently chosen color.
      const markSoldOut = () => {
        const chosen = selects.map((s) => s.value);
        selects.forEach((sel, idx) => {
          Array.from(sel.options).forEach((opt) => {
            let usable;
            if (colorIdx < 0 || idx === colorIdx) {
              usable = variants.some(
                (v) => v.available && variantOpts(v)[idx] === opt.value
              );
            } else {
              usable = variants.some((v) => {
                if (!v.available) return false;
                const opts = variantOpts(v);
                return opts[idx] === opt.value && opts[colorIdx] === chosen[colorIdx];
              });
            }
            opt.disabled = !usable;
          });
        });
      };

      // Smart-cascade: if the user's pick makes the current combo unavailable,
      // switch the OTHER selects to values from the first available variant
      // that has the user's picked value. Fully-OOS values are HTML-disabled
      // in markSoldOut, so this always finds a target.
      const cascade = (changedIdx) => {
        const chosen = selects.map((s) => s.value);
        if (hasAvailableCombo(chosen)) return;
        const pickedValue = chosen[changedIdx];
        const target = variants.find(
          (v) => v.available && variantOpts(v)[changedIdx] === pickedValue
        );
        if (!target) return;
        const opts = variantOpts(target);
        selects.forEach((s, i) => {
          if (i !== changedIdx && s.value !== opts[i]) s.value = opts[i];
        });
      };

      // Init: if Liquid rendered the slot with an unavailable default
      // (metafield's default_variant_id can land on a sold-out variant),
      // jump to the first fully available variant.
      const fixInitialState = () => {
        const chosen = selects.map((s) => s.value);
        if (hasAvailableCombo(chosen)) return;
        const target = variants.find((v) => v.available);
        if (!target) return;
        const opts = variantOpts(target);
        selects.forEach((s, i) => {
          if (s.value !== opts[i]) s.value = opts[i];
        });
      };

      const onChange = (event) => {
        const changedIdx = event ? selects.indexOf(event.currentTarget) : -1;
        if (changedIdx >= 0) {
          cascade(changedIdx);
        } else {
          fixInitialState();
        }
        markSoldOut();
        const chosen = selects.map((s) => s.value);
        const match = variants.find((v) => {
          const opts = variantOpts(v);
          if (opts.length !== chosen.length) return false;
          return opts.every((o, i) => o === chosen[i]);
        });
        log('slot match', { slotIdx: slot.dataset.slotIdx, chosen, match: match && { id: match.id, price: match.price, compare: match.compare_at_price, available: match.available } });
        slot.dataset.variantAvailable = match && match.available ? '1' : '0';
        syncAtcButton();
        if (!match) return;

        slot.dataset.resolvedVariantId = String(match.id);
        slot.dataset.resolvedPrice = String(match.price);
        slot.dataset.resolvedComparePrice = match.compare_at_price ? String(match.compare_at_price) : '';

        const isGift = slot.dataset.isGift === '1';
        const priceEl = slot.querySelector('[data-meluya-bundle-price]');
        log('price update', { slotIdx: slot.dataset.slotIdx, isGift, priceEl: !!priceEl });
        if (priceEl) {
          const currentEl = priceEl.querySelector('[data-meluya-bundle-current]');
          const compareEl = priceEl.querySelector('[data-meluya-bundle-compare]');

          if (isGift) {
            // Gift slot: only one price visible (compare-styled, struck) =
            // max(price, compare). The badge stays in the DOM untouched.
            let struck = match.price;
            if (match.compare_at_price && match.compare_at_price > match.price) {
              struck = match.compare_at_price;
            }
            if (compareEl) compareEl.textContent = moneyFormat(struck);
          } else {
            // Paid slot: current on the right, compare (struck) on the left.
            if (currentEl) currentEl.textContent = moneyFormat(match.price);
            if (match.compare_at_price && match.compare_at_price > match.price) {
              if (compareEl) {
                compareEl.textContent = moneyFormat(match.compare_at_price);
              } else {
                const span = document.createElement('span');
                span.className = 'meluya-bundle__price-compare';
                span.dataset.meluyaBundleCompare = '';
                span.textContent = moneyFormat(match.compare_at_price);
                priceEl.insertBefore(span, priceEl.firstChild);
              }
            } else if (compareEl) {
              compareEl.remove();
            }
          }
        }

        // Image: prefer the matched variant's featured_image. Shopify often
        // attaches the image to only one variant per color, so for variants
        // without a direct image we score every variant that has an image
        // by how many option values it shares with the user's selection
        // and pick the highest-scoring one.
        let imgSrc = null;
        if (match.featured_image && match.featured_image.src) {
          imgSrc = match.featured_image.src;
        } else {
          let best = null;
          let bestScore = 0;
          for (const v of variants) {
            if (!v.featured_image || !v.featured_image.src) continue;
            const opts = [v.option1, v.option2, v.option3];
            let score = 0;
            for (let i = 0; i < chosen.length; i += 1) {
              if (opts[i] === chosen[i]) score += 1;
            }
            if (score > bestScore) {
              bestScore = score;
              best = v;
            }
          }
          if (best) imgSrc = best.featured_image.src;
        }
        log('image lookup', { chosen, matchHasImg: !!(match.featured_image && match.featured_image.src), imgSrc });

        if (imgSrc) {
          const imgWrap = slot.querySelector('[data-meluya-bundle-image]');
          if (imgWrap) {
            const existingImg = imgWrap.querySelector('img');
            if (existingImg) {
              existingImg.src = imgSrc;
              existingImg.srcset = '';
            } else {
              imgWrap.innerHTML = '';
              const img = document.createElement('img');
              img.src = imgSrc;
              img.loading = 'lazy';
              img.width = 88;
              img.height = 88;
              img.alt = '';
              imgWrap.appendChild(img);
            }
          }
        }
      };

      selects.forEach((s) => s.addEventListener('change', onChange));

      // Initial pass: mark sold-out / auto-skip / sync price+image. The
      // metafield's default variant_id may be unavailable; this catches it.
      onChange();
    });

    // ===== Hauptpreis-Block syncen =====
    // Slots tragen ihre aufgeloesten Variant-Preise als data-attrs. Wir
    // summieren live, wenden die Discount-Logik aus dem Metafield an und
    // schreiben das Ergebnis in den theme-eigenen .price-display-Block.
    function computeBundleTotal() {
      let totalAll = 0;
      let totalPaid = 0;
      let totalCompare = 0;
      slots.forEach((slot) => {
        const price = parseInt(slot.dataset.resolvedPrice, 10);
        if (isNaN(price)) return;
        const compareRaw = parseInt(slot.dataset.resolvedComparePrice, 10);
        const compareAt = !isNaN(compareRaw) && compareRaw > price ? compareRaw : price;
        totalAll += price;
        totalCompare += compareAt;
        if (slot.dataset.isGift !== '1') totalPaid += price;
      });

      let total = totalAll;
      switch (cfg.discount_type) {
        case 'free_gift':
          total = totalPaid;
          break;
        case 'percentage':
          total = Math.round(totalAll * (1 - (Number(cfg.discount_value) || 0) / 100));
          break;
        case 'fixed_amount':
          total = totalAll - Math.round((Number(cfg.discount_value) || 0) * 100);
          break;
        case 'fixed_price':
          if (cfg.totals && cfg.totals.price != null) {
            total = Math.round(Number(cfg.totals.price) * 100);
          }
          break;
      }
      if (total < 0) total = 0;
      return { total, compare: totalCompare };
    }

    function updateMainPrice() {
      const totals = computeBundleTotal();
      const section = document.querySelector('.shopify-section--main-product');
      const scope = section || document;
      const currentEl = scope.querySelector('.price-display__current');
      const compareEl = scope.querySelector('.price-display__compare');
      const savingsEl = scope.querySelector('.price-display__savings');
      const savingsWrap = savingsEl ? savingsEl.closest('.price-display__right') : null;

      log('main price', totals);

      if (currentEl) currentEl.textContent = moneyFormat(totals.total);

      const hasDiscount = totals.compare > totals.total;
      if (compareEl) {
        if (hasDiscount) {
          compareEl.textContent = moneyFormat(totals.compare);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
      if (savingsEl) {
        if (hasDiscount) {
          if (savingsEl.dataset.savingsDisplay === 'percent' && totals.compare > 0) {
            const pct = Math.round(((totals.compare - totals.total) / totals.compare) * 100);
            savingsEl.textContent = 'du sparst ' + pct + '%';
          } else {
            savingsEl.textContent = 'du sparst ' + moneyFormat(totals.compare - totals.total);
          }
        }
        if (savingsWrap) savingsWrap.style.display = hasDiscount ? '' : 'none';
        else savingsEl.style.display = hasDiscount ? '' : 'none';
      }
    }

    // Re-run whenever any slot's resolved price changes — easiest hook is a
    // single MutationObserver on the slots container, watching data-attrs.
    const slotsContainer = document.querySelector('[data-meluya-bundle]');
    if (slotsContainer) {
      const mainPriceObserver = new MutationObserver(() => updateMainPrice());
      slotsContainer.querySelectorAll('[data-meluya-bundle-slot]').forEach((slot) => {
        mainPriceObserver.observe(slot, { attributes: true, attributeFilter: ['data-resolved-price', 'data-resolved-compare-price'] });
      });
    }

    // Initial sync
    updateMainPrice();

    const buildItems = () =>
      slots.map((slot) => {
        const id = parseInt(slot.dataset.resolvedVariantId, 10);
        const props = { _meluya_bundle_id: cfg.rule_id };
        if (slot.dataset.isGift === '1') props._meluya_bundle_gift = 'true';
        return { id, quantity: 1, properties: props };
      });

    const submitBundle = async (form) => {
      const items = buildItems();
      log('submit', { items });

      const submitButtons = Array.from(
        form.querySelectorAll('button[type="submit"], [type="submit"]')
      );
      submitButtons.forEach((b) => b.setAttribute('aria-busy', 'true'));

      const sectionsToBundle = new Set();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:start', { bubbles: true })
      );

      let response;
      let responseJson;
      try {
        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections: [...sectionsToBundle].join(','),
          }),
        });
        responseJson = await response.json();
      } catch (err) {
        warn('cart/add fetch failed', err);
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );
        submitButtons.forEach((b) => b.removeAttribute('aria-busy'));
        return;
      }

      submitButtons.forEach((b) => b.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:end', { bubbles: true })
      );

      log('cart/add response', response.status, responseJson);

      if (!response.ok) {
        form.dispatchEvent(
          new CustomEvent('cart:error', {
            bubbles: true,
            detail: { error: responseJson && responseJson.message },
          })
        );
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: { message: (responseJson && responseJson.message) || 'Fehler', tone: 'error' },
          })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );
        return;
      }

      const settings = (window.themeVariables && window.themeVariables.settings) || {};
      if (settings.cartType === 'page' || settings.pageType === 'cart') {
        window.location.href = `${root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${root}cart.js`)).json();
      cartContent.sections = responseJson.sections;
      const addedItems = responseJson.items ? responseJson.items : [responseJson];

      form.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: addedItems, cart: cartContent },
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent },
        })
      );

      if (settings.cartType === 'message') {
        const strings = (window.themeVariables && window.themeVariables.strings) || {};
        const tpl = strings.addedToCart || '{{product_title}}';
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: {
              message: tpl.replace('{{product_title}}', (addedItems[0] && addedItems[0].title) || ''),
            },
          })
        );
      }
    };

    const onSubmit = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      submitBundle(event.currentTarget);
    };

    const enableAtcButtons = (form) => {
      // The bundle placeholder product may render a disabled ATC if the variant
      // is unavailable. We're going to swap items at submit time anyway, so
      // re-enable.
      form.querySelectorAll('button[disabled], [aria-disabled="true"]').forEach((btn) => {
        if (btn.type === 'submit' || btn.tagName === 'BUTTON') {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('button--disabled', 'is-disabled');
        }
      });
    };

    const attach = (form) => {
      if (form.__meluyaBundleAttached) return;
      form.__meluyaBundleAttached = true;
      form.addEventListener('submit', onSubmit, true);
      enableAtcButtons(form);
      log('attached to form', form);

      // Click fallback in case the theme intercepts submit before us or the
      // submit button was rendered disabled.
      form.querySelectorAll('button[type="submit"], [type="submit"]').forEach((btn) => {
        btn.addEventListener(
          'click',
          (e) => {
            if (form.__meluyaBundleSubmitting) return;
            form.__meluyaBundleSubmitting = true;
            e.preventDefault();
            e.stopImmediatePropagation();
            submitBundle(form).finally(() => {
              form.__meluyaBundleSubmitting = false;
            });
          },
          true
        );
      });

      // Sticky-ATC and other buttons that point at the form via `form="<id>"`.
      if (form.id) {
        document
          .querySelectorAll(`button[type="submit"][form="${form.id}"], [type="submit"][form="${form.id}"]`)
          .forEach((btn) => {
            btn.removeAttribute('disabled');
            btn.removeAttribute('aria-disabled');
            btn.addEventListener(
              'click',
              (e) => {
                if (form.__meluyaBundleSubmitting) return;
                form.__meluyaBundleSubmitting = true;
                e.preventDefault();
                e.stopImmediatePropagation();
                submitBundle(form).finally(() => {
                  form.__meluyaBundleSubmitting = false;
                });
              },
              true
            );
          });
      }
    };

    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (isBundleForm(form)) attach(form);
    });
    // enableAtcButtons in attach() always re-enables; re-apply the slot-aware
    // disable so an initial unavailable variant doesn't leave the ATC clickable.
    syncAtcButton();

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('form[action*="/cart/add"]') && isBundleForm(node)) {
            attach(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
              if (isBundleForm(form)) attach(form);
            });
          }
        });
      }
      syncAtcButton();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
